"use client"

import { Reveal } from "../components/reveal"
import { useTr } from "../lib/use-tr"
import { AD_PLATFORM_ICONS } from "../logged-in/campaigns/[id]/lead-card"
import {
  ClipboardList,
  Linkedin,
  Mail,
  Megaphone,
  PenLine,
  Phone,
  Search,
  Send,
  Sparkles,
  TrendingUp,
  TriangleAlert,
  Zap,
} from "lucide-react"
import {
  type CSSProperties,
  type ComponentType,
  type ReactNode,
  type RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import { cn } from "@/lib/utils"

import { CASE_STUDY_DETAIL_T } from "./[slug]/translations"
import type { Lead } from "@/nango/types/apps.autoleads/autoleads/AutoLeads"
import type {
  CaseStudyExample,
  CaseStudyFact,
  CaseStudyFactKind,
  CaseStudySalesViewLocale,
} from "@/nango/types/apps.autoleads/autoleads/CaseStudy"

type IconCmp = ComponentType<{ className?: string; "aria-hidden"?: boolean }>
type Channel = CaseStudySalesViewLocale["sequence"][number]["channel"]
type Tr = (key: keyof typeof CASE_STUDY_DETAIL_T) => string

const CHANNEL_ICON: Record<Channel, IconCmp> = {
  mail: Mail,
  linkedin: Linkedin,
  call: Phone,
}

const CHANNEL_LABEL = {
  mail: "salesViewChannelMail",
  linkedin: "salesViewChannelLinkedin",
  call: "salesViewChannelCall",
} as const

// The sheet's groups, in reading order, with how many facts each shows. A
// cited fact is always shown; the rest of the group fills up to the cap.
const FACT_GROUPS: {
  kind: CaseStudyFactKind
  title: keyof typeof CASE_STUDY_DETAIL_T
  icon: IconCmp
  max: number
}[] = [
  { kind: "search", title: "salesViewFactsSearch", icon: Search, max: 3 },
  {
    kind: "broken",
    title: "salesViewFactsBroken",
    icon: TriangleAlert,
    max: 3,
  },
  { kind: "geo", title: "salesViewFactsGeo", icon: Sparkles, max: 2 },
  { kind: "signal", title: "salesViewFactsSignal", icon: TrendingUp, max: 3 },
]

const SIGNAL_ADS = "signal:ads"
const SIGNAL_HIRING = "signal:hiring"
const EXAMPLE_ORDER: CaseStudyExample["channel"][] = ["mail", "call"]

// The arrows are drawn across the two-column desktop layout only; on a single
// column the numbers alone pair each passage with its fact.
const LINK_OVERLAY_MEDIA = "(min-width: 1024px)"
// Arrows run down the gap between the step titles and the panels: the first
// lane sits this far left of the panels, each further lane one step further.
const LANE_INSET = 10
const LANE_STEP = 8
const LANE_RADIUS = 10
// The arrowhead stops this far short of the line it points at.
const LINK_END_GAP = 6

type Citation = {
  n: number
  fact: CaseStudyFact
  channel: CaseStudyExample["channel"]
  quote: string
}

/**
 * The pipeline a case study sells, in three linked steps: what the audit found
 * on one business, the email and call script written from those findings, then
 * the sequence that sends itself. Each passage of the copy that reuses a
 * finding is highlighted and numbered, and on desktop an arrow runs from the
 * finding down to that passage.
 */
export function SalesViewSteps({
  view,
  lead,
  facts,
}: {
  view: CaseStudySalesViewLocale
  lead: Lead | null
  facts: CaseStudyFact[]
}) {
  const tr = useTr(CASE_STUDY_DETAIL_T)
  const containerRef = useRef<HTMLDivElement>(null)
  const [active, setActive] = useState<string | null>(null)

  const factsById = new Map(facts.map((fact) => [fact.id, fact]))
  const examples = EXAMPLE_ORDER.flatMap(
    (channel) =>
      (view.examples ?? []).find((example) => example.channel === channel) ??
      [],
  )
  // Numbered in reading order; a fact cited twice keeps its first number.
  const citations: Citation[] = []
  const numbers = new Map<string, number>()
  for (const example of examples) {
    for (const link of example.links) {
      const fact = factsById.get(link.fact)
      if (!fact) continue
      if (!numbers.has(fact.id)) numbers.set(fact.id, numbers.size + 1)
      citations.push({
        n: numbers.get(fact.id) ?? 0,
        fact,
        channel: example.channel,
        quote: link.quote,
      })
    }
  }
  const linksKey = citations
    .map((citation) => `${citation.fact.id}>${citation.channel}`)
    .join("|")
  const links = useMemo(
    () =>
      linksKey
        .split("|")
        .filter(Boolean)
        .map((entry) => {
          const [fact, channel] = entry.split(">")
          return { fact, channel }
        }),
    [linksKey],
  )

  const mails = view.sequence.filter((step) => step.channel === "mail")
  const people = lead?.people?.length ?? 0
  const auditCount = lead?.website_quality_breakdown?.opportunities?.count ?? 0

  const steps: {
    icon: IconCmp
    title: string
    body: string
    aside?: ReactNode
    visual: ReactNode
  }[] = [
    {
      icon: ClipboardList,
      title: tr("salesViewFiche"),
      body: tr("salesViewFicheBody"),
      aside:
        people > 0 || auditCount > 0 ? (
          <p className="text-muted-foreground font-mono text-xs tabular-nums">
            {tr("salesViewSummary")
              .replace("{c}", String(people))
              .replace("{a}", String(auditCount))}
          </p>
        ) : null,
      visual:
        facts.length > 0 ? (
          <FactSheet
            facts={facts}
            numbers={numbers}
            active={active}
            onHover={setActive}
            tr={tr}
          />
        ) : (
          <FicheRows rows={view.fiche} />
        ),
    },
    {
      icon: PenLine,
      title: tr("salesViewDrafts"),
      body: tr("salesViewDraftsBody"),
      visual: (
        <ExamplesVisual
          examples={examples}
          citations={citations}
          mails={mails}
          tips={view.phoning}
          contacts={lead?.mailing_contacts ?? 0}
          active={active}
          onHover={setActive}
          tr={tr}
        />
      ),
    },
    {
      icon: Send,
      title: tr("salesViewAuto"),
      body: tr("salesViewAutoBody"),
      visual: <SequenceTimeline steps={view.sequence} tr={tr} />,
    },
  ]

  return (
    <div ref={containerRef} className="relative mx-auto max-w-5xl">
      <ol className="flex flex-col">
        {steps.map((step, idx) => {
          const Icon = step.icon
          const last = idx === steps.length - 1
          return (
            <li key={step.title} className="relative flex gap-4 sm:gap-6">
              {/* The rail: one numbered node per step, joined down to the next. */}
              <div className="flex flex-col items-center">
                <span className="bg-primary-cta text-primary-cta-foreground ring-background relative z-10 inline-flex size-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold tabular-nums ring-4">
                  {idx + 1}
                </span>
                {last ? null : (
                  <span
                    aria-hidden
                    className="border-primary-cta/40 my-1 w-px flex-1 border-l-2 border-dashed"
                  />
                )}
              </div>
              <div
                className={cn(
                  "grid min-w-0 flex-1 gap-5 lg:grid-cols-[minmax(0,15rem)_minmax(0,1fr)] lg:gap-10",
                  !last && "pb-14 sm:pb-20",
                )}
              >
                <Reveal className="flex flex-col gap-4 pt-1.5">
                  <div>
                    <div className="text-muted-foreground mb-2 flex items-center gap-2 text-xs font-medium">
                      <Icon className="text-primary-cta size-3.5" aria-hidden />
                      <span className="font-mono tracking-wide uppercase">
                        {tr("salesViewStepLabel").replace(
                          "{n}",
                          String(idx + 1),
                        )}
                      </span>
                    </div>
                    <h3 className="text-foreground font-display tracking-dense text-xl font-semibold sm:text-2xl">
                      {step.title}
                    </h3>
                    <p className="text-muted-foreground text-2sm mt-2 leading-relaxed">
                      {step.body}
                    </p>
                  </div>
                  {step.aside}
                </Reveal>
                <Reveal y={24} className="min-w-0">
                  <div
                    data-sv-panel
                    className="bg-muted/40 ring-border/40 rounded-2xl p-3 ring-1 sm:p-5"
                  >
                    {step.visual}
                  </div>
                </Reveal>
              </div>
            </li>
          )
        })}
      </ol>
      <LinkOverlay containerRef={containerRef} links={links} active={active} />
    </div>
  )
}

/** The small numbered disc that pairs a fact with the passage reusing it. */
function CiteNumber({ n, className }: { n: number; className?: string }) {
  return (
    <span
      className={cn(
        "bg-primary-cta text-primary-cta-foreground text-3xs inline-flex size-4 shrink-0 items-center justify-center rounded-full font-semibold tabular-nums",
        className,
      )}
    >
      {n}
    </span>
  )
}

function Card({
  className,
  children,
}: {
  className?: string
  children: ReactNode
}) {
  return (
    <div
      className={cn(
        "bg-background ring-border/60 rounded-xl shadow-sm ring-1",
        className,
      )}
    >
      {children}
    </div>
  )
}

/** The right-hand figure of a fact row: the measured number, kept short. */
function FactValue({ fact, tr }: { fact: CaseStudyFact; tr: Tr }) {
  if (fact.kind === "search") {
    return (
      <span className="flex shrink-0 items-center gap-1.5 font-mono text-xs tabular-nums">
        <span className="text-muted-foreground">
          {tr("salesViewPerMonth").replace("{n}", String(fact.volume ?? 0))}
        </span>
        <span className="bg-destructive/10 text-destructive rounded px-1.5 py-0.5">
          {fact.position
            ? tr("salesViewPosition").replace("{n}", String(fact.position))
            : tr("salesViewAbsent")}
        </span>
      </span>
    )
  }
  if (fact.id === SIGNAL_ADS && fact.platforms?.length) {
    return (
      <span className="text-muted-foreground flex shrink-0 items-center gap-1">
        {fact.platforms.map((platform) => {
          const PlatformIcon =
            AD_PLATFORM_ICONS[platform as keyof typeof AD_PLATFORM_ICONS] ??
            Megaphone
          return <PlatformIcon key={platform} className="size-3.5" />
        })}
      </span>
    )
  }
  if (fact.id === SIGNAL_HIRING && fact.count) {
    return (
      <span className="text-muted-foreground shrink-0 font-mono text-xs tabular-nums">
        {fact.count}
      </span>
    )
  }
  if (fact.metric) {
    return (
      <span className="text-destructive shrink-0 font-mono text-xs tabular-nums">
        {fact.metric}
      </span>
    )
  }
  return null
}

function factLabel(fact: CaseStudyFact, tr: Tr): string {
  if (fact.id === SIGNAL_ADS) return tr("salesViewSignalAds")
  if (fact.id === SIGNAL_HIRING) return tr("salesViewSignalHiring")
  return fact.label
}

function FactSheet({
  facts,
  numbers,
  active,
  onHover,
  tr,
}: {
  facts: CaseStudyFact[]
  numbers: Map<string, number>
  active: string | null
  onHover: (factId: string | null) => void
  tr: Tr
}) {
  const groups = FACT_GROUPS.map((group) => {
    const ofKind = facts.filter((fact) => fact.kind === group.kind)
    const cited = ofKind.filter((fact) => numbers.has(fact.id))
    const rest = ofKind.filter((fact) => !numbers.has(fact.id))
    return {
      ...group,
      facts: [...cited, ...rest].slice(0, Math.max(group.max, cited.length)),
    }
  }).filter((group) => group.facts.length > 0)

  // One column on purpose: every row starts at the sheet's left edge, so an
  // arrow leaving a cited row crosses no other row on its way out.
  return (
    <Card className="divide-border/60 divide-y">
      {groups.map((group) => {
        const GroupIcon = group.icon
        return (
          <section key={group.kind} className="px-3 py-2.5">
            <p className="text-muted-foreground text-2xs mb-1.5 flex items-center gap-1.5 px-1 font-mono tracking-wide uppercase">
              <GroupIcon className="size-3.5 shrink-0" aria-hidden />
              {tr(group.title)}
            </p>
            <ul className="flex flex-col gap-0.5">
              {group.facts.map((fact) => {
                const n = numbers.get(fact.id)
                return (
                  <li
                    key={fact.id}
                    data-sv-source={fact.id}
                    title={fact.detail || undefined}
                    onMouseEnter={() => n && onHover(fact.id)}
                    onMouseLeave={() => n && onHover(null)}
                    className={cn(
                      "text-2sm flex items-center gap-2 rounded-md px-1 py-1 transition-colors duration-300",
                      n && "cursor-default",
                      active === fact.id && "bg-primary-cta/10",
                    )}
                  >
                    {n ? (
                      <CiteNumber n={n} />
                    ) : (
                      <span
                        aria-hidden
                        className="bg-border mx-1.25 size-1.5 shrink-0 rounded-full"
                      />
                    )}
                    <span
                      className={cn(
                        "min-w-0 flex-1",
                        n
                          ? "text-foreground font-medium"
                          : "text-muted-foreground",
                      )}
                    >
                      {factLabel(fact, tr)}
                    </span>
                    <FactValue fact={fact} tr={tr} />
                  </li>
                )
              })}
            </ul>
          </section>
        )
      })}
    </Card>
  )
}

function FicheRows({ rows }: { rows: CaseStudySalesViewLocale["fiche"] }) {
  return (
    <Card className="p-4">
      <dl className="divide-border/60 divide-y">
        {rows.map((row) => (
          <div key={row.label} className="flex gap-3 py-2 first:pt-0 last:pb-0">
            <dt className="text-muted-foreground text-2xs w-28 shrink-0 pt-0.5 font-mono tracking-wide uppercase">
              {row.label}
            </dt>
            <dd className="text-foreground text-2sm min-w-0">{row.value}</dd>
          </div>
        ))}
      </dl>
    </Card>
  )
}

/**
 * The excerpt with every cited passage marked and numbered. Quotes are
 * verbatim substrings (the server checked them), placed in text order and
 * never overlapping.
 */
function MarkedText({
  text,
  citations,
  active,
  onHover,
}: {
  text: string
  citations: Citation[]
  active: string | null
  onHover: (factId: string | null) => void
}) {
  const spans = citations
    .map((citation) => ({ citation, start: text.indexOf(citation.quote) }))
    .filter(({ start }) => start >= 0)
    .sort((a, b) => a.start - b.start)
  const parts: ReactNode[] = []
  let cursor = 0
  for (const { citation, start } of spans) {
    if (start < cursor) continue
    parts.push(text.slice(cursor, start))
    const end = start + citation.quote.length
    parts.push(
      <mark
        key={`${citation.fact.id}-${start}`}
        data-sv-target={`${citation.fact.id}>${citation.channel}`}
        onMouseEnter={() => onHover(citation.fact.id)}
        onMouseLeave={() => onHover(null)}
        className={cn(
          "text-foreground decoration-primary-cta/60 bg-primary-cta/10 rounded-sm box-decoration-clone px-0.5 underline decoration-2 underline-offset-4 transition-colors duration-300",
          active === citation.fact.id && "bg-primary-cta/25",
        )}
      >
        {text.slice(start, end)}
        <CiteNumber
          n={citation.n}
          className="ml-1 align-text-top no-underline"
        />
      </mark>,
    )
    cursor = end
  }
  parts.push(text.slice(cursor))
  return <>{parts}</>
}

function ExampleWindow({
  icon: Icon,
  title,
  meta,
  footer,
  children,
}: {
  icon: IconCmp
  title: string
  meta?: string
  footer?: ReactNode
  children: ReactNode
}) {
  return (
    <Card className="overflow-hidden">
      <div className="bg-muted/50 text-muted-foreground border-border/60 flex items-center justify-between gap-2 border-b px-4 py-2 text-xs">
        <span className="text-foreground inline-flex items-center gap-1.5 font-medium">
          <Icon className="size-3.5" aria-hidden />
          {title}
        </span>
        {meta ? <span className="font-mono tabular-nums">{meta}</span> : null}
      </div>
      <p className="text-foreground text-2sm px-4 py-3.5 leading-7">
        {children}
      </p>
      {footer ? (
        <p className="text-muted-foreground border-border/60 border-t px-4 py-2 text-xs tabular-nums">
          {footer}
        </p>
      ) : null}
    </Card>
  )
}

function ExamplesVisual({
  examples,
  citations,
  mails,
  tips,
  contacts,
  active,
  onHover,
  tr,
}: {
  examples: CaseStudyExample[]
  citations: Citation[]
  mails: CaseStudySalesViewLocale["sequence"]
  tips: string[]
  contacts: number
  active: string | null
  onHover: (factId: string | null) => void
  tr: Tr
}) {
  const mailFooter =
    contacts > 1
      ? tr("salesViewMailsContacts")
          .replace("{n}", String(mails.length))
          .replace("{c}", String(contacts))
      : tr("salesViewMailsCount").replace("{n}", String(mails.length))
  const scriptFooter = tr("salesViewScriptCount").replace(
    "{n}",
    String(tips.length),
  )
  // Pages written before the excerpts existed show the cadence's first email
  // and the first script point, unlinked.
  const shown: { channel: CaseStudyExample["channel"]; text: string }[] =
    examples.length > 0
      ? examples
      : [
          ...(mails[0]
            ? [{ channel: "mail" as const, text: mails[0].intent }]
            : []),
          ...(tips[0] ? [{ channel: "call" as const, text: tips[0] }] : []),
        ]
  return (
    <div className="flex flex-col gap-3">
      {shown.map((example) => {
        const own = citations.filter(
          (citation) => citation.channel === example.channel,
        )
        const isMail = example.channel === "mail"
        return (
          <ExampleWindow
            key={example.channel}
            icon={isMail ? Mail : Phone}
            title={
              isMail ? tr("salesViewMailExample") : tr("salesViewCallExample")
            }
            meta={
              isMail && mails[0]
                ? tr("salesViewDay").replace("{n}", String(mails[0].day))
                : undefined
            }
            footer={isMail ? mailFooter : scriptFooter}
          >
            <MarkedText
              text={example.text}
              citations={own}
              active={active}
              onHover={onHover}
            />
          </ExampleWindow>
        )
      })}
    </div>
  )
}

/**
 * The arrows from each cited fact to the line of copy that reuses it. They
 * leave the fact sideways, run down the gap left of the panels in their own
 * lane and turn in at the passage's line, so no arrow crosses a card. Drawn
 * from measured positions; nothing renders on the server or on one column.
 */
function LinkOverlay({
  containerRef,
  links,
  active,
}: {
  containerRef: RefObject<HTMLDivElement | null>
  links: { fact: string; channel: string }[]
  active: string | null
}) {
  const [paths, setPaths] = useState<
    { fact: string; key: string; d: string }[]
  >([])
  const [still, setStill] = useState(true)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const wide = window.matchMedia(LINK_OVERLAY_MEDIA)
    setStill(window.matchMedia("(prefers-reduced-motion: reduce)").matches)
    let frame = 0
    const measure = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        const panel = container.querySelector("[data-sv-panel]")
        if (!wide.matches || !panel) {
          setPaths([])
          return
        }
        const origin = container.getBoundingClientRect()
        const laneBase = panel.getBoundingClientRect().left - origin.left
        const measured = links.flatMap((link) => {
          const from = container.querySelector(
            `[data-sv-source="${CSS.escape(link.fact)}"]`,
          )
          const to = container.querySelector(
            `[data-sv-target="${CSS.escape(`${link.fact}>${link.channel}`)}"]`,
          )
          const line = to?.getClientRects()[0]
          const text = to?.closest("p")?.getBoundingClientRect()
          if (!from || !line || !text) return []
          const a = from.getBoundingClientRect()
          return [
            {
              link,
              sx: a.left - origin.left,
              sy: a.top + a.height / 2 - origin.top,
              tx: text.left - origin.left - LINK_END_GAP,
              ty: line.top + line.height / 2 - origin.top,
            },
          ]
        })
        // The highest fact takes the outermost lane, so a lower fact's stub
        // never has to cross a lane already running down past it.
        const byHeight = [...measured].sort((a, b) => a.sy - b.sy)
        setPaths(
          measured.map((m) => {
            const lane = measured.length - 1 - byHeight.indexOf(m)
            const lx = laneBase - LANE_INSET - lane * LANE_STEP
            const r = Math.min(LANE_RADIUS, Math.abs(m.ty - m.sy) / 2)
            return {
              fact: m.link.fact,
              key: `${m.link.fact}>${m.link.channel}`,
              d: `M ${m.sx} ${m.sy} H ${lx + r} Q ${lx} ${m.sy} ${lx} ${m.sy + r} V ${m.ty - r} Q ${lx} ${m.ty} ${lx + r} ${m.ty} H ${m.tx}`,
            }
          }),
        )
      })
    }
    measure()
    // The steps rise into place as they scroll in, so positions move with the
    // scroll as well as with the layout.
    const observer = new ResizeObserver(measure)
    observer.observe(container)
    window.addEventListener("scroll", measure, { passive: true })
    wide.addEventListener("change", measure)
    void document.fonts?.ready.then(measure)
    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
      window.removeEventListener("scroll", measure)
      wide.removeEventListener("change", measure)
    }
  }, [containerRef, links])

  if (paths.length === 0) return null
  return (
    <svg
      aria-hidden
      className="text-primary-cta pointer-events-none absolute inset-0 z-20 size-full overflow-visible"
    >
      <defs>
        {/* A slim notched head, filled, sized in px so it never scales with
            the stroke. */}
        <marker
          id="sales-view-head"
          viewBox="0 0 10 10"
          refX="9"
          refY="5"
          markerWidth="9"
          markerHeight="9"
          markerUnits="userSpaceOnUse"
          orient="auto"
        >
          <path d="M 0 1 L 10 5 L 0 9 L 3 5 Z" fill="currentColor" />
        </marker>
      </defs>
      {paths.map(({ fact, key, d }) => {
        const lit = active === fact
        return (
          <g
            key={key}
            className="transition-opacity duration-300"
            opacity={active !== null && !lit ? 0.2 : 1}
          >
            <path
              d={d}
              fill="none"
              stroke="currentColor"
              strokeOpacity={lit ? 0.9 : 0.45}
              strokeWidth={lit ? 2 : 1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              markerEnd="url(#sales-view-head)"
            />
            <circle
              cx={d.split(" ")[1]}
              cy={d.split(" ")[2]}
              r="3"
              fill="currentColor"
            />
            {/* A bright stretch travels from the fact to the copy. */}
            {still ? null : (
              <path
                d={d}
                pathLength={100}
                fill="none"
                stroke="currentColor"
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeDasharray="12 200"
                className="sales-view-flow"
              />
            )}
          </g>
        )
      })}
    </svg>
  )
}

function SequenceTimeline({
  steps,
  tr,
}: {
  steps: CaseStudySalesViewLocale["sequence"]
  tr: Tr
}) {
  const channels = new Set(steps.map((step) => step.channel))
  const lastDay = steps.reduce((max, step) => Math.max(max, step.day), 0)
  const figures = [
    { value: steps.length, label: tr("salesViewTouches") },
    { value: channels.size, label: tr("salesViewChannels") },
    { value: lastDay, label: tr("salesViewDays") },
  ]
  return (
    <Card className="flex flex-col gap-5 p-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex gap-6">
          {figures.map((figure) => (
            <p key={figure.label} className="flex flex-col">
              <span className="text-foreground font-display text-2xl font-semibold tabular-nums sm:text-3xl">
                {figure.value}
              </span>
              <span className="text-muted-foreground text-xs">
                {figure.label}
              </span>
            </p>
          ))}
        </div>
        <span className="bg-success/10 text-success-text inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium">
          <Zap className="size-3.5" aria-hidden />
          {tr("salesViewAutoBadge")}
        </span>
      </div>
      {/* Each touch sits at its real day on the track, so the cadence reads at
          a glance; the intent stays in the tooltip to keep the track wordless.
          The track fills and the touches pop in as it scrolls into view. */}
      <div className="relative mx-4 pb-12">
        <div className="bg-border absolute inset-x-0 top-[0.8125rem] h-0.5 rounded-full sm:top-[1.0625rem]">
          <div className="sales-view-track bg-primary-cta/60 h-full rounded-full" />
        </div>
        <ol className="relative h-7 sm:h-9">
          {steps.map((step, index) => {
            const ChannelIcon = CHANNEL_ICON[step.channel]
            const left = lastDay > 0 ? (step.day / lastDay) * 100 : 0
            return (
              <li
                key={`${step.day}-${step.channel}-${index}`}
                title={step.intent}
                className="absolute top-0 flex -translate-x-1/2 flex-col items-center gap-1.5"
                style={{ left: `${left}%` }}
              >
                <span
                  className="sales-view-pop bg-primary-cta text-primary-cta-foreground ring-background inline-flex size-7 items-center justify-center rounded-full ring-2 sm:size-9 sm:ring-4"
                  style={{ "--sales-view-at": left / 100 } as CSSProperties}
                >
                  <ChannelIcon className="size-3.5 sm:size-4" aria-hidden />
                </span>
                <span className="text-foreground text-2xs font-mono whitespace-nowrap">
                  {tr("salesViewDay").replace("{n}", String(step.day))}
                </span>
                <span className="text-muted-foreground text-3xs hidden whitespace-nowrap sm:block">
                  {tr(CHANNEL_LABEL[step.channel])}
                </span>
              </li>
            )
          })}
        </ol>
      </div>
    </Card>
  )
}
